import React, { useEffect, useState } from 'react';
import Modal from '../shared/Modal';
import { TextField, SegmentedField, SelectField } from '../shared/FormField';
import { DEPARTMENT_CATEGORY_OPTIONS } from '../../../api/departmentsApi';
import { parseApiError } from '../FoundationalTables/SpeciesLivestockRegistry/registryHelpers';

export default function EditDepartmentModal({ isOpen, onClose, onSave, row, parentOptions = [] }) {
  const [form, setForm] = useState({ name: '', category: 'department', parentDepartmentId: '' });
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    if (!row) return;
    setForm({
      name: row.name ?? '',
      category: row.category ?? 'department',
      parentDepartmentId: row.parentDepartmentId ?? '',
    });
    setStatus('idle');
    setErrMsg('');
    setSubmitting(false);
  }, [row]);

  const submit = async () => {
    if (submitting || !row?.id) return;

    if (!form.name.trim() || !form.category || (form.category === 'unit' && !form.parentDepartmentId)) {
      setErrMsg('Please complete the required fields before saving.');
      setStatus('error');
      return;
    }

    setSubmitting(true);
    setStatus('idle');
    setErrMsg('');

    try {
      await onSave({
        id: row.id,
        name: form.name.trim(),
        category: form.category,
        parentDepartmentId: form.category === 'unit' ? form.parentDepartmentId : null,
      });
      setStatus('success');
    } catch (error) {
      setErrMsg(parseApiError(error, 'Unable to update record.'));
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStatus('idle');
    setErrMsg('');
    setSubmitting(false);
    onClose();
  };

  // A parent department can't be re-parented to one of its own units, and a
  // record can't be its own parent — filter it out of the picklist.
  const availableParentOptions = parentOptions.filter((option) => option.value !== row?.id);

  return (
    <Modal
      title="Edit Department / Unit"
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={submit}
      status={status}
      errorMessage={errMsg}
      onContinue={handleClose}
      submitLabel="Save Changes"
      successTitle="Record Updated"
      successMessage="The department/unit changes were saved."
      isSubmitting={submitting}
    >
      <SegmentedField
        label="Category"
        required
        value={form.category}
        onChange={(value) => setForm((current) => ({ ...current, category: value, parentDepartmentId: value === 'unit' ? current.parentDepartmentId : '' }))}
        options={DEPARTMENT_CATEGORY_OPTIONS}
      />
      <TextField
        label="Name"
        required
        value={form.name}
        onChange={set('name')}
        placeholder="e.g. Dairy Production"
      />
      {form.category === 'unit' && (
        <SelectField
          label="Parent Department"
          required
          value={form.parentDepartmentId}
          onChange={set('parentDepartmentId')}
          options={availableParentOptions}
          placeholder="Select parent department"
        />
      )}
    </Modal>
  );
}
